"use client";

import { useEffect, useMemo } from "react";
import { useTheme } from "next-themes";
import {
  currentDocumentTheme,
  isEmbedThemeMessage,
  merkeThemeVomWirt,
} from "../lib/embed-theme";
import { useIsEmbedded } from "./useIsEmbedded";

/**
 * Darf eine postMessage von diesem Origin das Theme setzen?
 *
 * `frame-ancestors` regelt, WER uns einbetten darf — aber ein Empfaenger sollte den
 * Absender trotzdem pruefen, statt jeder Nachricht zu glauben. Leere Suffix-Liste =
 * keine Pruefung (Rueckwaertskompatibilitaet).
 *
 * Kein naives `endsWith`: `evil-janeway.io` endet auf `janeway.io`, ist aber ein
 * fremder Host. Geprueft wird gegen `.<suffix>` bzw. exakte Gleichheit.
 */
export function isAllowedEmbedOrigin(origin: string, suffixes: readonly string[]): boolean {
  if (suffixes.length === 0) return true;
  let host: string;
  try {
    host = new URL(origin).hostname;
  } catch {
    return false;
  }
  return suffixes.some((raw) => {
    const bare = raw.startsWith(".") ? raw.slice(1) : raw;
    return host === bare || host.endsWith(`.${bare}`);
  });
}

/**
 * Child-Seite der Theme-Bruecke: folgt dem Theme der einbettenden Seite, solange die App
 * in einem iframe laeuft. Ausserhalb eines iframes ein No-Op.
 *
 * `allowedOriginSuffix` grenzt die Absender ein (z.B. ".janewaylabs.com" und ".janeway.io"
 * fuer Labs+Prod): `frame-ancestors` regelt zwar schon, WER uns einbetten darf, aber
 * postMessage-Empfaenger sollten den Absender trotzdem pruefen, statt jeder Nachricht
 * zu glauben.
 */
export function useEmbeddedThemeBridge(allowedOriginSuffix?: string | string[]): void {
  const embedded = useIsEmbedded();
  const { setTheme } = useTheme();

  // Stabile Referenz: ein frisches Array pro Render wuerde den Effect neu binden.
  const suffixes = useMemo(
    () => (allowedOriginSuffix === undefined ? [] : Array.isArray(allowedOriginSuffix) ? allowedOriginSuffix : [allowedOriginSuffix]),
    [allowedOriginSuffix],
  );
  const suffixKey = suffixes.join(",");

  useEffect(() => {
    if (!embedded) return;

    function onMessage(event: MessageEvent) {
      if (!isAllowedEmbedOrigin(event.origin, suffixKey ? suffixKey.split(",") : [])) return;
      if (!isEmbedThemeMessage(event.data)) return;

      const theme = event.data.__janewayArtifactTheme;

      // Steht es schon so da, ist nichts zu tun — und vor allem darf dann auch
      // nichts angemerkt werden: `setTheme` mit dem aktuellen Wert loest keinen
      // Effekt aus, die Marke bliebe liegen, und der naechste echte Klick des
      // Nutzers auf genau diesen Wert wuerde still verschluckt.
      if (theme === currentDocumentTheme()) return;

      // ANGEWENDET, NICHT GESPEICHERT: erst anmerken, dann setzen. Der Provider
      // kann einer `resolvedTheme`-Aenderung nicht ansehen, woher sie kam —
      // diese Marke ist die einzige Stelle, an der es noch bekannt ist.
      // Begruendung und Messung: `lib/embed-theme.ts`.
      merkeThemeVomWirt(theme);
      setTheme(theme);
    }

    window.addEventListener("message", onMessage);
    // HIER WIRD DIE MARKE NICHT WEGGERAEUMT, und das ist wichtig genug fuer
    // einen Kommentar: dieser Effekt haengt an `setTheme`, und dessen Identitaet
    // wechselt bei einem Render. Ein `vergissThemeVomWirt()` im Aufraeumer lief
    // deshalb GENAU ZWISCHEN `setTheme` und dem Persistenz-Effekt des Providers
    // — die Marke war weg, bevor sie jemand abholen konnte, und das Leck war
    // wieder offen. Am 2026-09-16 eingebaut und an der Sonde im Provider
    // gemessen (`vomWirt=false`, obwohl gerade angemerkt worden war).
    //
    // Weggeraeumt werden muss sie auch nicht: sie lebt im Modul des
    // EINGEBETTETEN Dokuments und stirbt mit ihm. Innerhalb eines lebenden
    // Dokuments holt der Persistenz-Effekt sie immer ab, weil oben
    // kurzgeschlossen wird, wenn sich gar nichts aendert.
    return () => window.removeEventListener("message", onMessage);
  }, [embedded, setTheme, suffixKey]);
}
